import React from "react";
import { colors, suspiciousColor } from "@client/constants";
import { useDeviceTopicLive } from "@client/hooks/useDeviceTopicLive";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { useTopic } from "@client/hooks/useTopic";
import { formatMetric, metricLabel } from "@client/utils/formatMetric";
import Dot from "./elementary/Dot";

export type ValueVariant = "lg" | "md" | "sm";
/** Prahy pro tónování hodnoty (jako device overview: SOC<20 red, <50 amber; teplota >75 red, >60 amber). */
export type ValueTone = { warnBelow?: number; badBelow?: number; warnAbove?: number; badAbove?: number };

type ValueProps = {
  topicId: string;
  /** Hustota/měřítko — STEJNÉ primitivum velké (dashboard) i drobné (buňka tabulky). Default lg = dnešní vzhled. */
  variant?: ValueVariant;
  /** Freshness Dot. Default true (dashboard). Device dlaždice ho neměly → false. */
  dot?: boolean;
  /** true = modrá colors[0] (dashboard); false = foreground (bílá, jako device dlaždice). Default true. */
  accent?: boolean;
  /** Tónování dle prahů (přebíjí accent; offline přebíjí vše). */
  tone?: ValueTone;
};

/** Třídy per varianta. lg = velká dashboard karta (parita), md = overview dlaždice (text-xl), sm = drobná buňka. */
const VARIANT: Record<ValueVariant, { wrap: string; label: string; value: string }> = {
  lg: { wrap: "flex flex-col gap-1 p-3 m-1 flex-1 min-w-40", label: "text-sm font-medium text-muted-foreground truncate", value: "text-2xl font-bold tabular-nums" },
  md: { wrap: "flex min-w-0 flex-col gap-0.5 p-1", label: "truncate text-xs text-muted-foreground", value: "text-xl font-bold tabular-nums" },
  sm: { wrap: "flex min-w-0 flex-col gap-0 px-1.5 py-1", label: "truncate text-[10px] text-muted-foreground", value: "text-sm font-semibold tabular-nums" },
};

/** "" = bez tónu (použije se accent/foreground). Tón přítomen → v pořádku = green, jinak amber/red. */
const toneClassFor = (n: number, tone: ValueTone): string => {
  if (!Number.isFinite(n)) return "";
  if (tone.badBelow != null && n < tone.badBelow) return "text-red-600";
  if (tone.badAbove != null && n > tone.badAbove) return "text-red-600";
  if (tone.warnBelow != null && n < tone.warnBelow) return "text-amber-600";
  if (tone.warnAbove != null && n > tone.warnAbove) return "text-amber-600";
  return "text-green-600";
};

/**
 * Adaptivní zobrazovač jedné MQTT hodnoty — JEDNO primitivum napříč měřítky. Plochá karta, nickname + bold
 * `tabular-nums` hodnota; offline = červená přeškrtnutá; Dot (volitelně) = freshness. Reprodukuje dashboard
 * (lg, modrá, Dot) i device overview dlaždici (md, foreground/tón, bez Dot). Formátování přes `formatMetric`.
 */
export const Value: React.FC<ValueProps> = ({ topicId, variant = "lg", dot = true, accent = true, tone }) => {
  const slow = useTopic(topicId);
  const live = useDeviceTopicLive(topicId); // rychlá ~1 Hz hodnota z device telemetrie (device stránky); undefined → pomalá MQTT
  const meta = useMetricMeta(topicId);
  const v = VARIANT[variant] ?? VARIANT.lg;

  // Preferuj živou hodnotu (čerstvá, rychlá); při výpadku (stale) fallback na MQTT → zůstává `suspicious` přeškrtnutí.
  const useLive = live !== undefined && !live.stale;
  const value = useLive ? live.value : slow.value;
  const suspicious = useLive ? false : slow.suspicious;
  const { animationDuration, lastValueInterval } = slow;

  const numeric = typeof value === "number" ? value : Number(value);
  const toneClass = !suspicious && tone ? toneClassFor(numeric, tone) : "";
  const useAccent = !suspicious && !toneClass && accent;
  const color = suspicious ? suspiciousColor : useAccent ? colors[0] : undefined;

  return (
    <div className={v.wrap}>
      <h2 className={v.label}>{metricLabel(meta, topicId)}</h2>
      <p className={`${v.value} ${toneClass} ${suspicious ? "line-through decoration-amber-600" : ""}`} style={color ? { color } : undefined}>
        {dot && <Dot duration={animationDuration ?? 0} interval={lastValueInterval ?? 0} />}
        {formatMetric(value, meta, { fallback: "---" })}
      </p>
    </div>
  );
};

export default React.memo(Value);
